// View areas (feature catalogue B3): named parts of the board, shared by everyone on it, listed under
// the board map. A click shows that part; ★ makes it the view the board opens with; ✕ deletes it.
// "+ Ansicht" saves what is on screen now (lanewerk-core board/views.rs).
import type { Store } from "./store";

export interface ViewArea { id: string; name: string; x: number; y: number; w: number; h: number; default: boolean }

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

export class ViewList {
  private list = $("#lw-views-list");
  private add = $<HTMLButtonElement>("#lw-views-add");
  private form = $<HTMLFormElement>("#lw-views-form");
  private name = $<HTMLInputElement>("#lw-views-name");
  private key = "";

  /**
   * @param current the board rect on screen now
   * @param show brings a board rect into view
   */
  constructor(private store: Store, private current: () => [number, number, number, number], private show: (r: [number, number, number, number]) => void) {
    store.subscribe((source) => { if (source !== "view") this.render(); });
    this.add.addEventListener("click", () => {
      this.form.hidden = !this.form.hidden;
      if (!this.form.hidden) { this.name.value = ""; this.name.focus(); }
    });
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const name = this.name.value.trim();
      if (!name) return this.name.focus();
      const [x, y, w, h] = this.current();
      const id = `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
      if (this.store.saveView(id, name, x, y, w, h)) this.form.hidden = true;
    });
    this.form.addEventListener("keydown", (e) => {
      e.stopPropagation(); // board shortcuts stay off while typing
      if (e.key === "Escape") this.form.hidden = true;
    });
    this.render();
  }

  render() {
    const views = this.store.views();
    const ro = this.store.readOnly;
    this.add.hidden = ro;
    if (ro) this.form.hidden = true;
    const key = JSON.stringify(views) + ro;
    if (key === this.key) return;
    this.key = key;
    this.list.replaceChildren(...views.map((v) => {
      const li = el("li", "view-item");
      const go = el("button", "view-go", v.name || "Ohne Namen");
      go.type = "button";
      go.title = `${v.name} zeigen`;
      go.addEventListener("click", () => this.show([v.x, v.y, v.w, v.h]));
      const star = el("button", "view-icon", v.default ? "★" : "☆");
      star.type = "button";
      star.title = v.default ? "Nicht mehr beim Öffnen zeigen" : "Beim Öffnen des Boards zeigen";
      star.setAttribute("aria-label", `${star.title}: ${v.name}`);
      star.setAttribute("aria-pressed", String(v.default));
      star.disabled = ro;
      star.addEventListener("click", () => this.store.setDefaultView(v.default ? "" : v.id));
      const del = el("button", "view-icon", "✕");
      del.type = "button";
      del.title = "Ansicht löschen";
      del.setAttribute("aria-label", `Ansicht ${v.name} löschen`);
      del.disabled = ro;
      del.addEventListener("click", () => this.store.deleteView(v.id));
      if (v.default) li.classList.add("default");
      li.append(go, star, del);
      return li;
    }));
    $("#lw-views-empty").hidden = views.length > 0;
  }
}
